/**
 * Utilities for detecting unsaved changes in deck-builder-style forms.
 *
 * Extracted from `useDeckBuilder` so the same comparison can be reused by
 * other "edit this list" flows (the list pages want to gate their back-nav
 * leave-confirm on the same "is anything actually different" signal).
 */

interface DeckMetadata {
  name: string
  description: string
  leaderId: number | null
}

interface DeckCardEntry {
  card: { id: number }
  quantity: number
}

/**
 * Return true if any of `name`, `description`, `leaderId`, or the ordered
 * (cardId, quantity) list differs between `current` and `initial`.
 */
export function hasUnsavedDeckChanges(
  current: { metadata: DeckMetadata; cards: DeckCardEntry[] },
  initial: { metadata: DeckMetadata; cards: DeckCardEntry[] },
): boolean {
  if (current.metadata.name !== initial.metadata.name) return true
  if (current.metadata.description !== initial.metadata.description) return true
  if (current.metadata.leaderId !== initial.metadata.leaderId) return true
  if (current.cards.length !== initial.cards.length) return true

  const initialMap = new Map(initial.cards.map((c) => [c.card.id, c.quantity]))
  for (const lc of current.cards) {
    if (initialMap.get(lc.card.id) !== lc.quantity) return true
  }
  return false
}

/**
 * "New deck" variant — there's no server state to diff against, so "unsaved"
 * just means the user has started filling anything in.
 */
export function hasUnsavedNewDeckContent(name: string, description: string, cards: DeckCardEntry[]): boolean {
  return cards.length > 0 || name.trim() !== '' || description.trim() !== ''
}
